import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const RoomTypeCard = ({ 
  roomType, 
  onEdit, 
  onDelete, 
  onAddProduct, 
  onEditProduct, 
  onDeleteProduct,
  onProductOrderChange 
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  const { 
    attributes, 
    listeners, 
    setNodeRef, 
    transform, 
    transition, 
    isDragging 
  } = useSortable({ id: roomType.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 30 : 'auto',
  };

  const products = roomType.products || [];
  const defaultProduct = products.find(p => p.기본상품);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
        <div className="flex items-center gap-2 p-3">
          {/* Drag Handle */}
          <button
            className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 p-1"
            {...listeners}
            {...attributes}
            title="드래그하여 순서 변경"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
            </svg>
          </button>

          {/* Expand/Collapse Button */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-gray-600 hover:text-gray-800 transition-transform duration-200"
            style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Room Type Info - Clickable */}
          <div 
            className="flex-1 cursor-pointer hover:bg-white/50 rounded-md p-1 -m-1 transition-colors"
            onClick={() => onEdit(roomType)}
          >
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-gray-900">
                {roomType.객실명}
              </h3>
              {roomType.영문명 && (
                <span className="text-sm text-gray-500">({roomType.영문명})</span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-600">
              <span>기준 {roomType.기준인원}명 / 최대 {roomType.최대인원}명</span>
              {roomType.어메니티 && (
                <span className="text-xs text-gray-500">
                  {roomType.어메니티.split(',').slice(0, 2).join(', ')}
                  {roomType.어메니티.split(',').length > 2 && '...'}
                </span>
              )}
              <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                상품 {products.length}개
              </span>
            </div>
          </div>

          {/* Edit hint icon */}
          <div className="text-gray-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Products List (Expandable) */}
      {isExpanded && (
        <div className="p-4 bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <span className="text-xs text-gray-500">객실</span>
              <span className="text-xs text-gray-400">›</span>
              <span className="text-xs text-gray-500">상품</span>
              <span className="ml-2 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs">총 {products.length}개</span>
            </h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {products.map((product, index) => (
              <div
                key={product.id || index}
                onClick={() => onEditProduct(roomType, product)}
                className="group flex items-start gap-2 p-2 bg-white rounded border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
              >
             
                {/* Content */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <h5 className="font-medium text-gray-900 truncate">{product.상품명}</h5>
                    {product.기본상품 && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700">기본</span>
                    )}
                    {product.확정방식 && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        product.확정방식 === '자동확정' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {product.확정방식}
                      </span>
                    )}
                    {product.환불가능여부 && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        product.환불가능여부 === '환불가능' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {product.환불가능여부}
                      </span>
                    )}
                  </div>

                  {/* Compact meta */}
                  <div className="mt-0.5 text-[11px] text-gray-500 flex items-center gap-3">
                    <span>
                      서비스 {typeof product.포함서비스수 === 'number' ? product.포함서비스수 : (Array.isArray(product.포함서비스) ? product.포함서비스.length : 0)}개
                    </span>
                  </div>
                </div>

                {/* Click hint icon */}
                <div className="text-gray-300 group-hover:text-gray-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}

            <div
              onClick={() => onAddProduct(roomType)}
              className="group flex items-start gap-2 p-2 bg-white rounded border-2 border-dashed border-gray-300 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onAddProduct(roomType);
              }}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-xs text-gray-700 truncate">상품 추가</span>
                </div>
                <div className="mt-0.5 text-[11px] text-gray-500 flex items-center gap-3">
                  <span>새 상품을 등록하세요</span>
                </div>
              </div>

              <div className="text-gray-300 group-hover:text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomTypeCard;
